import { useState } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PropTypes from 'prop-types';

/**
 * PasswordField - Reusable password input with show/hide toggle
 * 
 * Wraps Material-UI TextField with password visibility toggle icon
 * 
 * @component
 * @example
 * <PasswordField
 *   label="Şifre"
 *   name="password"
 *   value={formData.password}
 *   onChange={handleChange}
 *   error={!!errors.password}
 *   helperText={errors.password}
 * />
 */
const PasswordField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
  autoComplete,
  autoFocus,
  fullWidth = true,
  margin = 'normal',
  required = false,
  placeholder,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    // Prevent default to avoid focus loss on IconButton click
    event.preventDefault();
  };

  return (
    <TextField
      fullWidth={fullWidth}
      margin={margin}
      label={label}
      name={name}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      helperText={helperText}
      disabled={disabled}
      autoComplete={autoComplete}
      autoFocus={autoFocus}
      required={required}
      placeholder={placeholder}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
              onClick={handleTogglePasswordVisibility}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              disabled={disabled}
              size="large"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
};

PasswordField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  fullWidth: PropTypes.bool,
  margin: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
};

export default PasswordField;

